/**
 * Camada 1 · painel de ajuda.
 *
 * O NAMESPACE E OBRIGATORIO EM TODA CHAMADA, e nao um parametro opcional com padrao.
 * Dois produtos apontando para o mesmo namespace passam a ver a ajuda um do outro, o que
 * e vazamento de conteudo entre produtos e nao recurso. Deixar o campo opcional seria
 * deixar esse defeito a um esquecimento de distancia.
 */

import type { Idioma } from '@complexity/i18n';

/** Um artigo da base de conhecimento, como o painel precisa dele. */
export interface ArtigoAjuda {
  readonly id: string;
  readonly titulo: string;
  /** Resumo de uma linha, para a lista. O corpo inteiro so e buscado ao abrir. */
  readonly resumo: string;
  /** Marcadores para o filtro. Vazio e valido. */
  readonly marcadores: readonly string[];
  /**
   * O idioma do texto que VEIO, e nao o que foi pedido (ADR 0060).
   *
   * Pedido `en` para um artigo ainda sem traducao, o servico devolve o original e escreve
   * `pt` aqui. E por este campo que a tela avisa "disponivel so em portugues", em vez de
   * mostrar portugues no modo ingles sem explicacao nenhuma.
   *
   * OPCIONAL porque a fonte em memoria (a degradacao declarada) nao tem traducao e nao tem o
   * que declarar: ausente se le como "nao houve queda a avisar".
   */
  readonly idioma?: Idioma;
}

/** O contrato que o painel usa para buscar. Uma interface, e nao uma chamada direta,
 *  porque o teste precisa conseguir devolver artigos sem subir services/knowledge.
 *
 *  `idioma` E OPCIONAL nos dois metodos: ausente, a fonte HTTP usa o idioma corrente da tela
 *  no instante da chamada, e a de memoria o ignora porque so tem o original. */
export interface FonteAjuda {
  buscar(namespace: string, termo: string, idioma?: Idioma): Promise<readonly ArtigoAjuda[]>;
  obter(namespace: string, id: string, idioma?: Idioma): Promise<string | null>;
}

/** Estado do painel, como o componente que o desenha precisa enxergar. */
export type EstadoAjuda =
  | { readonly tipo: 'ocioso' }
  | { readonly tipo: 'buscando' }
  | { readonly tipo: 'resultado'; readonly artigos: readonly ArtigoAjuda[] }
  /**
   * A fonte fora do ar NAO e a mesma coisa que busca sem resultado, e por isso sao dois
   * estados. Confundir os dois faria a tela dizer "nenhum artigo encontrado" quando o
   * servico caiu, e a pessoa procuraria o defeito na propria busca.
   */
  | { readonly tipo: 'indisponivel'; readonly motivo: string };

/** Roda a busca e traduz qualquer falha para o estado `indisponivel`, com o motivo. */
export async function buscarAjuda(
  fonte: FonteAjuda,
  namespace: string,
  termo: string,
  // O idioma que a tela esta mostrando. Ele vem de quem chama, e nao e lido aqui, para a
  // gaveta refazer a busca quando a pessoa troca o idioma com resultado aberto.
  idioma?: Idioma,
): Promise<EstadoAjuda> {
  if (namespace.trim() === '') {
    return {
      tipo: 'indisponivel',
      motivo: 'Namespace de ajuda não configurado para este produto.',
    };
  }
  try {
    const artigos = await fonte.buscar(namespace, termo, idioma);
    return { tipo: 'resultado', artigos };
  } catch {
    return {
      tipo: 'indisponivel',
      motivo: 'A base de conhecimento não respondeu. Tente novamente em instantes.',
    };
  }
}

/**
 * A TELA, acrescentada em 01/09/2026. O contrato acima existia desde 31/08 sem nenhum
 * componente que o consumisse, e o `package.json` registrava isso com todas as letras:
 * "nao tem importador nenhum hoje". Deixou de ser verdade.
 */
export { PainelAjuda } from './components/PainelAjuda.js';
export type { PainelAjudaProps } from './components/PainelAjuda.js';

/** A implementacao HTTP do contrato acima, para os produtos nao escreverem `fetch`. */
export { criarFonteAjudaHttp } from './fonte-http.js';

/**
 * A CENTRAL DE AJUDA, acrescentada em 03/09/2026.
 *
 * Ela e a tela dos NOVE PASSOS DO USUARIO do manual de casos de uso (UC-06 a UC-09). O
 * `PainelAjuda` acima continua existindo e nao foi tocado: ele e a gaveta de busca rapida
 * dentro do chassi, e a Central e a tela inteira, com categorias, conversa e historico.
 *
 * OS DOIS CONTRATOS CONVIVEM: quem so precisa de busca implementa `FonteAjuda`; quem serve a
 * Central implementa `FonteCentral`. Fundir os dois quebraria o painel do prontidao, que ja
 * consome o primeiro.
 */
export { CentralDeAjuda } from './components/CentralDeAjuda.js';
export type { CentralDeAjudaProps } from './components/CentralDeAjuda.js';
export {
  ROTULO_DA_SITUACAO,
  ROTULO_DO_TIPO,
  type CategoriaAjuda,
  type ConversaAjuda,
  type FonteCentral,
  type NovaConversa,
  type SituacaoDaConversa,
  type TipoDeConversa,
  type TurnoDaConversa,
} from './central.js';

/**
 * A `FonteCentral` que fala com os DOIS servicos: conhecimento e mensagens.
 *
 * ELA E A SAIDA DA MEMORIA. O cabecalho de `fonte-central-http.ts` registra a medicao que a
 * produziu e a decisao de arquitetura que ela carrega: as tres de conversa sao do
 * `services/messaging`, que ja tem as tabelas e as rotas, e duplica-las no `services/knowledge`
 * criaria duas fontes para a mesma conversa.
 */
export { criarFonteCentralHttp } from './fonte-central-http.js';
export type { ConfiguracaoDaCentral, CredenciaisCentral } from './fonte-central-http.js';

/** A fonte em memoria, que continua existindo para a SUITE exercitar a tela sem subir os dois
 *  servicos. Para o produto, quem atende agora e a de cima. */
export { criarFonteCentralEmMemoria } from './fonte-memoria.js';
export type { AcervoDaBase } from './fonte-memoria.js';

/** O tipo do artigo de acervo. Ele nasce em `fonte-memoria`, e nao num arquivo de produto:
 *  morar dentro de um produto fazia a remocao daquele produto quebrar quem nao tinha nada
 *  a ver com ele. */
export type { ArtigoDaBase } from './fonte-memoria.js';

/**
 * O DICIONARIO DA AJUDA REGISTRA-SE SOZINHO, na carga do modulo.
 *
 * POR QUE AQUI E NAO EM CADA APP: registrar e responsabilidade de quem TEM as frases, e quem
 * as tem e este pacote. E o mesmo padrao de `packages/ui/src/index.ts:203` e de
 * `packages/auth/src/index.ts:67`, e nao uma terceira forma de fazer a mesma coisa.
 *
 * `registrarDicionario` FUNDE em vez de substituir (`packages/i18n/src/index.ts:75`), entao
 * a Ajuda registra as dela e convive com as do produto: nenhum app perde traducao por este
 * pacote ser importado, e nenhum app precisa saber que estas frases existem.
 *
 * EFEITO NA IMPORTACAO, e ele e deliberado: importar `@complexity/help` ja deixa a Ajuda
 * traduzida, no produto que existe hoje e no que nascer amanha. A alternativa era copiar
 * estas frases para o `en.ts` de cada app, que e exatamente o que o item 2 da regra das
 * cinco camadas proibe.
 */
import { registrarDicionario } from '@complexity/i18n';
import { EN_AJUDA } from './i18n/en.js';

registrarDicionario('en', EN_AJUDA);

export { EN_AJUDA } from './i18n/en.js';
